import { Controller } from "@hotwired/stimulus"
import { offeredLocationIds, pickLocation, placesText } from "../../utils/stock_item_location"

// On a line of a stock movement, choosing the item selects the location it is
// stored at and says where it sits, with the quantity held at each place.
// The storekeeper may still choose another location: among those that hold the
// item for an exit or a transfer, among all of them for an entry. One
// controller for the form of a movement (a line per row) and the form of a
// single line.
export default class extends Controller {
  static values = {
    category: String,
    storedAt: String,
    noLocation: String,
    noStock: String,
  }

  connect() {
    this.onChange = (event) => {
      if (event.target.matches('select[name$="[stock_item_id]"]')) this.itemChosen(event.target)
    }
    this.element.addEventListener("change", this.onChange)
    // A line that comes with its item (a draft reopened, a form sent back) says
    // where the item sits too, and keeps the location it came with.
    this.element.querySelectorAll('select[name$="[stock_item_id]"]').forEach((itemSelect) => {
      if (itemSelect.value) this.itemChosen(itemSelect, { keep: true })
    })
  }

  disconnect() {
    this.element.removeEventListener("change", this.onChange)
  }

  async itemChosen(itemSelect, { keep = false } = {}) {
    const line = itemSelect.closest("tr") || this.element
    const locationSelect = line.querySelector('select[name$="[location_id]"]')
    if (!locationSelect) return
    if (!itemSelect.value) return this.forget(locationSelect)

    const chosen = itemSelect.value
    const before = locationSelect.value
    const locations = await this.readLocations(chosen)
    // Another item was chosen while this one was being read.
    if (!itemSelect.isConnected || itemSelect.value !== chosen) return
    // The read failed: the list and the sentence of the item before would lie.
    if (!locations) return keep ? undefined : this.forget(locationSelect)

    // The location the line came with is listed whatever it holds today. One
    // chosen while the item was being read stays if the list offers it.
    const saved = keep ? locationSelect.value : ""
    const touched = locationSelect.value !== before
    this.offer(locationSelect, offeredLocationIds(locations, this.categoryValue), saved)
    const place = pickLocation(locations, this.categoryValue)
    if (!saved && !(touched && locationSelect.value)) {
      // No location to start from: the one of the item before does not stay.
      this.select(locationSelect, place && this.offers(locationSelect, place.id) ? place.id : "")
    }
    this.say(locationSelect, this.sentence(locations, place), locations.map((held) => held.full_name).join("\n"))
  }

  // The locations of the item, or null when they could not be read.
  async readLocations(itemId) {
    try {
      const locale = document.documentElement.lang || "fr"
      const response = await fetch(`/${locale}/schools/stocks/stock_items/${itemId}/get_price`, {
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return null

      return (await response.json()).locations || []
    } catch (_error) {
      return null
    }
  }

  // Nothing is known of the item of the line: the whole list comes back, the
  // sentence goes, and the location of the item before with them.
  forget(select) {
    if (select.allLocations) {
      this.offer(select, null)
      this.select(select, "")
    }
    this.say(select, "")
  }

  sentence(locations, place) {
    // « Choose one » is said to an entry only: an exit out of a location that
    // holds nothing is refused when the line is saved.
    if (locations.length === 0 && this.categoryValue === "entry") return this.noLocationValue
    if (!place) return this.noStockValue

    // A function: a « $ » in the name of a location is not a pattern.
    return this.storedAtValue.replace("%{places}", () => placesText(locations))
  }

  // Lists the given locations only, or all of them (null), and `also` when
  // given. The whole list is kept aside the first time, to be brought back for
  // another item.
  offer(select, ids, also = "") {
    select.allLocations ||= Array.from(select.options).map((option) => ({ value: option.value, text: option.text.trim() }))
    let kept = select.allLocations.filter((option) => option.value === "" || ids === null || ids.includes(option.value) || option.value === also)
    // The item sits only at locations this form does not list: the line is not
    // left with an empty list.
    if (!kept.some((option) => option.value !== "")) kept = select.allLocations
    const chosen = select.value
    const data = kept.map((option) => ({
      value: option.value,
      text: option.text,
      placeholder: option.value === "",
      selected: option.value === chosen,
    }))

    if (select.slim) return select.slim.setData(data)

    select.replaceChildren(...data.map((option) => new Option(option.text, option.value, false, option.selected)))
  }

  offers(select, id) {
    return Array.from(select.options).some((option) => option.value === String(id))
  }

  // The enhanced select keeps its own display: it is told, not only the field.
  select(select, id) {
    const value = String(id)
    if (select.slim) select.slim.setSelected(value)
    else select.value = value
  }

  // `detail`: the building, floor and room of each location, on hover.
  say(select, text, detail = "") {
    const cell = select.parentElement
    let hint = cell.querySelector("[data-stock-item-location-hint]")
    if (!hint) {
      hint = document.createElement("p")
      hint.className = "ds-field__hint"
      hint.dataset.stockItemLocationHint = ""
      hint.style.marginTop = "4px"
      cell.appendChild(hint)
    }
    hint.textContent = text
    hint.title = detail
    hint.hidden = text === ""
  }
}
